import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { CalendarPicker } from '@modules/common/components/DateRangePicker/CalendarPicker';
import { getCalendarTitleByMode } from '@modules/common/components/DateRangePicker/CalendarPicker/helpers';
import { getDateRange } from '@modules/common/components/DateRangePicker/helpers';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { EditableCompareFieldName } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/EditableCompareFieldName/EditableCompareFieldName';
import {
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_TO_CUSTOM_LAST_DURATION,
  DEFAULT_COMPARE_TO_PREVIOUS_PERIOD,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetFormattedDurationFieldLabels,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareToPreviousPeriodFromDuration,
  getDefaultCompareToCustomAbsoluteDuration,
  getPreviousPeriodValueAndUnitForOptionLabel,
  isValidQuery,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { DateRangeRounded, ExpandMoreRounded } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import { isEqual } from 'lodash';
import pluralize from 'pluralize';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const CompareDurationB = () => {
  const {
    tenant: { timezone },
  } = useGetUser();

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );

  const compareDurationBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationBName ||
      DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  const selectPreviousTimePeriod = (
    _: SyntheticEvent<Element, Event>,
    checked: boolean,
  ) => {
    if (checked) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          compareTo:
            duration.type === 'last'
              ? getCompareToPreviousPeriodFromDuration(duration)
              : DEFAULT_COMPARE_TO_PREVIOUS_PERIOD,
        },
      });

      dispatch({
        type: QuantifyActionType.SET_METADATA,
        payload: {
          shouldShowTopDataPoints: true,
          visibleDataPoints: [],
        },
      });

      setShouldFetchReportData(true);
      trackQuantifyCompareEvents({
        eventName:
          'Quantify Compare - Duration B Previous Period Option Selected',
        link: analysisLink,
      });
    }
  };

  const selectCustomDurationOption = (
    _: SyntheticEvent<Element, Event>,
    checked: boolean,
  ) => {
    if (checked) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          compareTo:
            duration.type === 'last'
              ? DEFAULT_COMPARE_TO_CUSTOM_LAST_DURATION
              : getDefaultCompareToCustomAbsoluteDuration(timezone),
        },
      });

      dispatch({
        type: QuantifyActionType.SET_METADATA,
        payload: {
          shouldShowTopDataPoints: true,
          visibleDataPoints: [],
        },
      });

      setShouldFetchReportData(true);
      trackQuantifyCompareEvents({
        eventName:
          'Quantify Compare - Duration B Custom Duration Option Selected',
        link: analysisLink,
      });
    }
  };

  const onCustomDurationChange = (newDuration: DateTimeRange) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        compareTo: {
          type: 'timerange',
          duration: newDuration,
        },
      },
    });

    dispatch({
      type: QuantifyActionType.SET_METADATA,
      payload: {
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
      },
    });

    setShouldFetchReportData(true);
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Duration B Custom Duration Changed',
      link: analysisLink,
    });
  };

  const customDurationDateRange =
    compareTo?.type === 'timerange'
      ? getDateRange(compareTo.duration)
      : ([null, null] as DateRange<Dayjs | null>);

  const previousPeriodLabel = getPreviousPeriodValueAndUnitForOptionLabel(
    compareTo,
    duration,
  );

  const onSave = (fieldBName: string) => {
    dispatch({
      type: QuantifyActionType.SET_METADATA,
      payload: {
        compareDurationField: {
          compareDurationBName: fieldBName,
        },
      },
    });
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Duration B renamed',
      filterName: fieldBName,
      link: analysisLink,
    });
  };

  const { compareDurationLabel } = useGetFormattedDurationFieldLabels({
    shouldShowForAllDurationTypes: true,
  });

  const shouldDisablePreviousPeriodOption = duration.type !== 'last';

  return (
    <Stack spacing={1} data-testid="quantify-compare-duration-field-b">
      <Stack
        direction="row"
        alignItems="center"
        columnGap={1}
        rowGap={1}
        flexWrap="wrap"
      >
        <EditableCompareFieldName
          initialFieldName={compareDurationBName}
          onSave={onSave}
        />
        {compareDurationLabel.secondary && (
          <Typography variant="caption" color="text.secondary">
            ({compareDurationLabel.secondary})
          </Typography>
        )}
      </Stack>
      <FormControl>
        <RadioGroup
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Tooltip
            arrow
            disableInteractive
            title={
              shouldDisablePreviousPeriodOption
                ? `Selecting a previous period based on Duration A is not supported for "Between" and "Since" date-ranges. Please input a custom value for Duration B.`
                : ''
            }
            placement="top"
          >
            <FormControlLabel
              sx={{ pb: 0.5 }}
              checked={compareTo?.type === 'offset'}
              onChange={selectPreviousTimePeriod}
              control={
                <Radio
                  size="small"
                  disabled={shouldDisablePreviousPeriodOption}
                  sx={{
                    py: 0.2,
                  }}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">
                    Previous {previousPeriodLabel.value}{' '}
                    {pluralize(
                      previousPeriodLabel.unit,
                      previousPeriodLabel.value,
                    )}
                  </Typography>
                </Stack>
              }
            />
          </Tooltip>
          <FormControlLabel
            checked={compareTo?.type === 'timerange'}
            onChange={selectCustomDurationOption}
            control={<Radio size="small" />}
            label={
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="body2">Custom</Typography>
                {compareTo?.type === 'timerange' && (
                  <CalendarPicker
                    shouldHideDisplayModes={duration.type !== 'last'}
                    dateRange={customDurationDateRange}
                    timeRange={compareTo.duration}
                    onChange={(newTimeRange) => {
                      if (!isEqual(compareTo.duration, newTimeRange)) {
                        onCustomDurationChange(newTimeRange);
                      }
                    }}
                    renderPickerInput={(label) => {
                      const tooltipTitle = getCalendarTitleByMode(
                        getDateRange(compareTo.duration),
                        compareTo.duration.type === 'absolute'
                          ? 'absolute'
                          : 'last',
                      );

                      return (
                        <Tooltip arrow title={tooltipTitle}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DateRangeRounded />}
                            endIcon={<ExpandMoreRounded />}
                          >
                            {label}
                          </Button>
                        </Tooltip>
                      );
                    }}
                  />
                )}
              </Stack>
            }
          />
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};
